import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "audio",
    "playButton",
    "playIcon",
    "pauseIcon",
    "progress",
    "progressFill",
    "progressHandle",
    "waveform",
    "currentTime",
    "duration",
    "volumeButton",
    "volumeSlider",
    "volumeFill",
    "speedButton",
    "speedMenu",
    "speedOption"
  ]

  static values = {
    playing: { type: Boolean, default: false },
    speed: { type: Number, default: 1 },
    volume: { type: Number, default: 1 },
    muted: { type: Boolean, default: false }
  }

  connect() {
    this.setupAudioEvents()
    this.generateWaveform()
  }

  disconnect() {
    if (this.audioTarget) {
      this.audioTarget.removeEventListener('timeupdate', this.boundTimeUpdate)
      this.audioTarget.removeEventListener('loadedmetadata', this.boundMetadataLoaded)
      this.audioTarget.removeEventListener('ended', this.boundEnded)
    }
  }

  setupAudioEvents() {
    this.boundTimeUpdate = this.handleTimeUpdate.bind(this)
    this.boundMetadataLoaded = this.handleMetadataLoaded.bind(this)
    this.boundEnded = this.handleEnded.bind(this)

    this.audioTarget.addEventListener('timeupdate', this.boundTimeUpdate)
    this.audioTarget.addEventListener('loadedmetadata', this.boundMetadataLoaded)
    this.audioTarget.addEventListener('ended', this.boundEnded)

    // If metadata already loaded
    if (this.audioTarget.readyState >= 1) {
      this.handleMetadataLoaded()
    }
  }

  generateWaveform() {
    if (!this.hasWaveformTarget) return

    // Generate pseudo-random waveform bars for visual effect
    const barCount = 80
    const bars = []

    for (let i = 0; i < barCount; i++) {
      // Create varied heights using a combination of sine waves for natural look
      const height = 20 + Math.sin(i * 0.3) * 15 + Math.sin(i * 0.7) * 10 + Math.random() * 20
      bars.push(`<div class="waveform-bar" style="height: ${Math.min(height, 60)}%"></div>`)
    }

    this.waveformTarget.innerHTML = bars.join('')
  }

  // Playback controls
  togglePlay() {
    if (this.playingValue) {
      this.pause()
    } else {
      this.play()
    }
  }

  play() {
    this.audioTarget.play()
    this.playingValue = true
    this.updatePlayButton()
  }

  pause() {
    this.audioTarget.pause()
    this.playingValue = false
    this.updatePlayButton()
  }

  updatePlayButton() {
    if (this.hasPlayIconTarget && this.hasPauseIconTarget) {
      if (this.playingValue) {
        this.playIconTarget.classList.add('hidden')
        this.pauseIconTarget.classList.remove('hidden')
      } else {
        this.playIconTarget.classList.remove('hidden')
        this.pauseIconTarget.classList.add('hidden')
      }
    }
  }

  // Time handling
  handleTimeUpdate() {
    const current = this.audioTarget.currentTime
    const duration = this.audioTarget.duration

    if (duration) {
      const percent = (current / duration) * 100
      this.updateProgress(percent)
      this.updateCurrentTime(current)
    }
  }

  handleMetadataLoaded() {
    this.updateDuration(this.audioTarget.duration)
  }

  handleEnded() {
    this.playingValue = false
    this.updatePlayButton()
    this.updateProgress(0)
  }

  updateProgress(percent) {
    if (this.hasProgressFillTarget) {
      this.progressFillTarget.style.width = `${percent}%`
    }
    if (this.hasProgressHandleTarget) {
      this.progressHandleTarget.style.left = `${percent}%`
    }
    // Update waveform played state
    if (this.hasWaveformTarget) {
      this.updateWaveformProgress(percent)
    }
  }

  updateWaveformProgress(percent) {
    const bars = this.waveformTarget.querySelectorAll('.waveform-bar')
    const playedBars = Math.floor((percent / 100) * bars.length)

    bars.forEach((bar, index) => {
      if (index < playedBars) {
        bar.style.background = '#a855f7'
      } else {
        bar.style.background = 'rgba(255, 255, 255, 0.25)'
      }
    })
  }

  updateCurrentTime(seconds) {
    if (this.hasCurrentTimeTarget) {
      this.currentTimeTarget.textContent = this.formatTime(seconds)
    }
  }

  updateDuration(seconds) {
    if (this.hasDurationTarget) {
      this.durationTarget.textContent = this.formatTime(seconds)
    }
  }

  formatTime(seconds) {
    if (isNaN(seconds) || !isFinite(seconds)) return '0:00'

    const hrs = Math.floor(seconds / 3600)
    const mins = Math.floor((seconds % 3600) / 60)
    const secs = Math.floor(seconds % 60)

    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  // Progress bar seeking
  seek(event) {
    const rect = this.progressTarget.getBoundingClientRect()
    const percent = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width))
    const time = percent * this.audioTarget.duration

    this.audioTarget.currentTime = time
  }

  startSeeking(event) {
    this.isSeeking = true
    this.seek(event)

    this.boundSeekMove = this.handleSeekMove.bind(this)
    this.boundSeekEnd = this.handleSeekEnd.bind(this)

    document.addEventListener('mousemove', this.boundSeekMove)
    document.addEventListener('mouseup', this.boundSeekEnd)
  }

  handleSeekMove(event) {
    if (this.isSeeking) {
      this.seek(event)
    }
  }

  handleSeekEnd() {
    this.isSeeking = false
    document.removeEventListener('mousemove', this.boundSeekMove)
    document.removeEventListener('mouseup', this.boundSeekEnd)
  }

  // Skip forward/backward
  skipBackward() {
    this.audioTarget.currentTime = Math.max(0, this.audioTarget.currentTime - 15)
  }

  skipForward() {
    this.audioTarget.currentTime = Math.min(this.audioTarget.duration, this.audioTarget.currentTime + 15)
  }

  // Volume controls
  toggleMute() {
    this.mutedValue = !this.mutedValue
    this.audioTarget.muted = this.mutedValue
    this.updateVolumeIcon()
  }

  setVolume(event) {
    const rect = this.volumeSliderTarget.getBoundingClientRect()
    const percent = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width))

    this.volumeValue = percent
    this.audioTarget.volume = percent
    this.mutedValue = percent === 0
    this.updateVolumeUI()
  }

  updateVolumeUI() {
    if (this.hasVolumeFillTarget) {
      this.volumeFillTarget.style.width = `${this.volumeValue * 100}%`
    }
    this.updateVolumeIcon()
  }

  updateVolumeIcon() {
    if (!this.hasVolumeButtonTarget) return

    const icon = this.volumeButtonTarget.querySelector('i')
    if (!icon) return

    icon.className = 'ph'
    if (this.mutedValue || this.volumeValue === 0) {
      icon.classList.add('ph-speaker-x')
    } else if (this.volumeValue < 0.5) {
      icon.classList.add('ph-speaker-low')
    } else {
      icon.classList.add('ph-speaker-high')
    }
  }

  // Speed controls
  toggleSpeedMenu() {
    if (this.hasSpeedMenuTarget) {
      this.speedMenuTarget.classList.toggle('open')
    }
  }

  setSpeed(event) {
    const speed = parseFloat(event.currentTarget.dataset.speed)
    this.speedValue = speed
    this.audioTarget.playbackRate = speed

    // Update active state on speed options
    this.speedOptionTargets.forEach(option => {
      option.classList.toggle('active', parseFloat(option.dataset.speed) === speed)
    })

    // Update button text
    if (this.hasSpeedButtonTarget) {
      this.speedButtonTarget.querySelector('.speed-label').textContent = `${speed}x`
    }

    // Close menu
    if (this.hasSpeedMenuTarget) {
      this.speedMenuTarget.classList.remove('open')
    }
  }

  // Close speed menu when clicking outside
  closeSpeedMenu(event) {
    if (this.hasSpeedMenuTarget && !event.target.closest('.speed-control')) {
      this.speedMenuTarget.classList.remove('open')
    }
  }
}
